// @flow
import React from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { HashLink } from "react-router-hash-link";

import { scrollToElement } from "Commons/Utilities/scrollTo";
import { formatCurrency } from "modules/Offers/misc/Utils";
import { TrustBanner } from "Commons/Components/TrustBanner/TrustBanner";
import { isMobile } from "Commons/Utilities";

import type { OfferT } from "modules/Offers/misc/Types";
import type { ReminderT } from "modules/Reminder/misc/Types";

import bestQuality from "flavours/app-remind.me/assets/icons/bestQuality.png";
import trustPilot from "Commons/Assets/images/trustpilotBIG.png";

import { Button } from "shared/formElements";
import NewTariff from "./NewTariff";
import OldTariff from "./OldTariff";

import Styles from "./TariffComparison.module.scss";

type Props = {
  reminder: ReminderT,
  offer: OfferT,
  remindMeBonus: Object,
  category: string,
  fullWidth?: boolean,
};

const TariffComparison = ({ reminder, offer, remindMeBonus, category, fullWidth }: Props) => {
  const { t } = useTranslation("offerPageDetail");
  const _savings = remindMeBonus?.amount ? remindMeBonus.totalSaving : offer?.gasElectricityDetails?.savings || 0;

  const newBenchmarkFactor = category === "strom" ? 1.3 : 2;

  const negativeSavingBenchmark = offer?.gasElectricityDetails?.benchmarkTariffTotalCostInEuro
    ? offer.gasElectricityDetails.benchmarkTariffTotalCostInEuro * newBenchmarkFactor
    : 0;

  const negativeSavingNewSavings = negativeSavingBenchmark - (offer?.gasElectricityDetails?.totalCostAmount || 0);

  const savingsBlock =
    negativeSavingNewSavings <= 0 ? (
      "Aktuell keine Ersparnis"
    ) : (
      <>
        <span>{t("offerBox.savings")}</span>
        <span>
          {_savings <= 0
            ? formatCurrency(negativeSavingNewSavings)
            : formatCurrency(
                remindMeBonus?.amount ? remindMeBonus.totalSaving : offer.gasElectricityDetails?.savings || 0
              )}
        </span>
      </>
    );

  return (
    <div className={Styles.tariffComparison}>
      <div className={classnames(Styles.responsiveContent, { [Styles.fullWidth]: fullWidth })}>
        <h2>{t("tariffsComparison.title")}</h2>
        <OldTariff reminder={reminder} offer={offer} category={category} />
        <NewTariff reminder={reminder} offer={offer} />
        <div className={classnames(Styles.savings, "theme-bgcolor-primary")}>{savingsBlock}</div>
        <div className={Styles.note}>{t("tariffsComparison.note")}</div>
        <div className={Styles.ctaBtnHolder}>
          <HashLink to="#CSF" scroll={(el) => scrollToElement(el, "smooth", -80)}>
            <Button fullWidth className={Styles.ctaBtn}>
              {t("changeServiceCTA")}
            </Button>
          </HashLink>
        </div>
        <br />
        {isMobile() ? (
          <div className={Styles.mobileTrustBunner}>
            <img src={bestQuality} alt="Best Quality" />
            <img src={trustPilot} alt="Trust Pilot" />
          </div>
        ) : (
          <TrustBanner />
        )}
      </div>
    </div>
  );
};

export default TariffComparison;
